/* eslint-disable */
// src/layouts/local/inactive/index.js

import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";

import localService from "services/localService";
import inactiveLocalTableData from "./data/inactiveLocalTableData";

function InactiveLocals() {
  const location = useLocation();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [localsDetails, setLocalsDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
  setIsLoading(true);
  try {
    const data = await localService.getInactiveComercioWithOwners();
    setLocalsDetails(data || []);
  } catch (e) {
    console.error("Error fetching inactive locals:", e);
    setLocalsDetails([]);
  } finally {
    setIsLoading(false);
  }
}, []);

useEffect(() => {
  load();
}, [load, location.key]);

useEffect(() => {
  const onFocus = () => load();
  const onVisibility = () => {
    if (document.visibilityState === "visible") load();
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}, [load]);

  const groupedByDepartment = useMemo(() => {
    const groups = {};
    (localsDetails || []).forEach((local) => {
      const dept = local.Department || "Sin departamento";
      if (!groups[dept]) groups[dept] = [];
      groups[dept] = [...groups[dept], local];
    });
    return groups;
  }, [localsDetails]);

  const getLocalDetailsDestination = (local) => {
    const id = local.Id ?? local.LocalId;
    return {
      pathname: `/local/${id}`,
      state: {
        local,
        returnTo: {
          pathname: location.pathname,
          search: location.search || "",
          hash: location.hash || "",
          state: location.state ?? null,
        },
      },
    };
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={3}>
        <Grid container spacing={6} rowSpacing={3}>
          {/* encabezado con botón volver a activos */}
          <Grid item xs={12} sx={{ mb: 3 }}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDTypography variant="h6" color="white">
                  Locales inactivos
                </MDTypography>
                <Button color="inherit" onClick={() => navigate("/local")}>
                  Volver a locales activos
                </Button>
              </MDBox>
            </Card>
          </Grid>

          {/* un card por departamento, dentro listas por ciudad */}
          {Object.entries(groupedByDepartment).map(([department, deptLocals]) => {
            // agrupamos locales del departamento por ciudad
            const groupedByCity = (deptLocals || []).reduce((acc, local) => {
              const city = local.City || "Sin ciudad";
              if (!acc[city]) acc[city] = [];
              acc[city].push(local);
              return acc;
            }, {});

            const sortedCities = Object.keys(groupedByCity).sort((a, b) =>
              a.localeCompare(b, "es")
            );

            return (
              <Grid item xs={12} key={department}>
                <Card sx={{ p: 2 }}>
                  <MDBox
                    mb={1.5}
                    px={1.5}
                    py={1.25}
                    borderRadius="md"
                    bgColor={darkMode ? "#263E5B" : "#CCE2F7"}
                  >
                    <MDTypography
                      variant="h6"
                      color="inherit"
                      fontWeight="bold"
                      sx={{ fontSize: "20px", color: darkMode ? "#E4F0FC" : "#174A78" }}
                    >
                      {department}
                    </MDTypography>
                  </MDBox>

                  {/* para cada ciudad, subtítulo + tabla propia */}
                  <MDBox>
                    {sortedCities.map((city, cityIndex) => {
                      const cityLocals = groupedByCity[city];

                      const { columns, rows } = inactiveLocalTableData({
                        localsDetails: cityLocals,
                        onDetails: getLocalDetailsDestination,
                        darkMode,
                      });

                      return (
                        <MDBox
                          key={`${department}-${city}`}
                          pt={cityIndex === 0 ? 0 : 2}
                        >
                          <MDBox
                            px={1.5}
                            py={1}
                            bgColor={darkMode ? "#2B3548" : "#E8EBEF"}
                            sx={{ borderBottom: `1px solid ${darkMode ? "#46546C" : "#DEE2E6"}` }}
                          >
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              color="inherit"
                              sx={{ display: "block", fontSize: "18px", textTransform: "none", color: darkMode ? "#EDF2F8" : "#344767" }}
                            >
                              {city}
                            </MDTypography>
                          </MDBox>

                          <MDBox
                            sx={({ palette }) => ({
                              "& .MuiTableContainer-root": { maxWidth: "100%", overflowX: "auto" },
                              "& table": { tableLayout: "fixed", width: "100%", minWidth: "600px" },
                              "& thead": {
                                position: "absolute",
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: "hidden",
                                clip: "rect(0, 0, 0, 0)",
                                clipPath: "inset(50%)",
                                whiteSpace: "nowrap",
                                border: 0,
                              },
                              "& th, & td": { boxSizing: "border-box", verticalAlign: "middle" },
                              "& th:first-of-type, & td:first-of-type": {
                                width: "60%",
                                padding: { xs: "8px 8px 8px 12px", sm: "8px 12px" },
                              },
                              "& th:nth-of-type(2), & td:nth-of-type(2)": {
                                width: "40%",
                                padding: { xs: "8px", sm: "8px 12px" },
                              },
                              "& tbody td > div": {
                                display: "block",
                                width: "100%",
                                maxWidth: "100%",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                              },
                              "& tbody td:nth-of-type(2) > div": {
                                whiteSpace: "nowrap",
                                overflowWrap: "normal",
                              },
                              "& tbody tr:not(:last-child) td": {
                                borderBottom: `1px solid ${darkMode ? "#3B4A63" : palette.grey[300]}`,
                              },
                              "& tbody tr:last-child td": { borderBottom: "none" },
                            })}
                          >
                            <DataTable
                              table={{ columns, rows }}
                              isLoading={isLoading}
                              isSorted={false}
                              entriesPerPage={false}
                              showTotalEntries={false}
                              noEndBorder
                            />
                          </MDBox>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InactiveLocals;
